/* eslint-disable react/no-danger */

import React from "react";
import {
  VariantSelector,
  Quantity,
  Button,
  Accordion,
  Feature,
  PdpUpsell,
  UpsellHintText
} from "~components";
import cn from "classnames";
import { useQuantity, useCartContext, useProductContext } from "~hooks";
import { PortableText } from "@portabletext/react";
import * as styles from "./styles.module.scss";

/** ============================================================================
 * @component
 * Product Information, shade & quantity selector and add to cart button that features on PDP.
 *
 * @param  {string}    				category  										Product category
 * @param  {string}    				productId  										Product ID
 * @param  {Variant[]}  			variants  										The product variants available
 * @param  {function}  				setVariants  									State update function
 * @param  {Feature[]}   			features 											List of key attributes
 * @param  {string}   				infoText 											More descriptive text with info
 * @param  {Accordion[]}   		accordions 										List of accordions with more info, FAQs etc
 * @param  {string}   				className 										For additional styling config
 * @return {node}
 */

const ShopModule = ({
  productId,
  category,
  infoText,
  features,
  accordions,
  variants,
  setVariants,
  className
}) => {
  const { addVariantsToCart, numberOfItemsInCart, getCartDiscountPercentage } =
    useCartContext();
  const {
    getVariantData,
    getProductData,
    isFetchingInventory,
    checkIsProductInStock,
    getPDPUpsellProducts
  } = useProductContext();

  const { comingSoon, title } = getProductData(productId) || {};

  const setSelectedVariant = (selectedVariantId) => {
    setVariants((prev) =>
      prev.map((variant) => ({
        ...variant,
        selected: variant.variantId === selectedVariantId
      }))
    );
  };

  const { quantity, increment, decrement } = useQuantity();

  const selectedVariant = variants.find((variant) => variant.selected);

  const isInsufficientStock = () => {
    const variantInventoryQuantity = getVariantData(
      selectedVariant?.variantId
    )?.inventoryQuantity;
    if (variantInventoryQuantity < quantity) return true;
    return false;
  };

  const handleAddToCart = () => {
    addVariantsToCart([selectedVariant.variantId], quantity);
  };

  const preDiscountedPrice = getProductData(productId)?.price.amount;
  const discountPercentageWithCurrentQuantity = getCartDiscountPercentage(
    numberOfItemsInCart + quantity
  );
  const discountedPrice =
    preDiscountedPrice -
    preDiscountedPrice * (discountPercentageWithCurrentQuantity / 100);
  const hasDiscount = preDiscountedPrice !== discountedPrice;
  const buttonDisplayPrice = hasDiscount ? discountedPrice : preDiscountedPrice;

  const isAddToCartButtonDisabled = () => {
    if (comingSoon) return true;
    if (isInsufficientStock()) return true;
    return false;
  };

  const isProductInStock = checkIsProductInStock(productId);

  const cartButtonContent = () => {
    if (comingSoon) {
      return `Coming soon`;
    }
    if (isProductInStock) {
      return `Add to cart — $${buttonDisplayPrice.toFixed(2)}`;
    }
    return `Out of stock`;
  };

  const upsellProducts = getPDPUpsellProducts(productId);

  return (
    <div className={cn(styles.container, className)}>
      <div className={cn(`caption`, styles.category)}>{category}</div>
      <h1 className={cn(`h2`, styles.title)}>{title}</h1>
      <div className={styles.selectionContainer}>
        {!comingSoon && (
          <VariantSelector
            className={styles.variantSelector}
            label="Select"
            variants={variants}
            onChange={setSelectedVariant}
            quantity={quantity}
          />
        )}

        {!comingSoon && (
          <>
            <UpsellHintText
              className={styles.upsellHint}
              quantityToAdd={quantity}
            />

            <Quantity
              value={quantity}
              handleIncrement={increment}
              handleDecrement={decrement}
              className={styles.quantity}
            />
          </>
        )}

        <Button
          light
          fluid
          disabled={isAddToCartButtonDisabled()}
          onClick={handleAddToCart}
          loading={isFetchingInventory}
          className={cn(styles.cartButton, `add-to-cart-button-lgf`)}
          strikethrough={
            hasDiscount ? `$${preDiscountedPrice?.toFixed(2)}` : null
          }
        >
          {cartButtonContent()}
        </Button>
      </div>

      {features?.[0] && (
        <div className={styles.features}>
          {features?.map((feature, i) => (
            <Feature
              key={i}
              text={feature.text}
              icon={feature.icon}
              className={styles.feature}
            />
          ))}
        </div>
      )}

      {infoText && (
        <div className={styles.infoText}>
          <PortableText value={infoText} />
        </div>
      )}

      {accordions?.[0] && (
        <div className={styles.accordions}>
          {accordions.map((accordion, i) => (
            <Accordion
              key={i}
              title={accordion.title}
              content={accordion.content}
            />
          ))}
        </div>
      )}

      {upsellProducts?.[0] && (
        <div className={styles.upsellContainer}>
          <h2 className={cn(`h2`, styles.upsellTitle)}>Bundle & Save</h2>
          <p className={cn(`b2`, styles.upsellText)}>
            Add with another product to save on checkout
          </p>
          {upsellProducts.map((id) => (
            <PdpUpsell
              key={id}
              productId={id}
              pdpProductId={productId}
              className={styles.pdpUpsell}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopModule;
